import { PropsWithChildren, useEffect } from 'react';
import { dispatchTopBarTraySelectionEvent } from '@frontend/shared';
import { createStoreWithPersist, createShallowStore } from '@frontend/store';
import { useCalendarStore } from './calendar.store';

export enum SchedulePulseTab {
  AppointmentRequests = 'schedule-appointment-requests-tab',
  Alerts = 'schedule-alerts-tab',
  AppointmentList = 'schedule-appointments-list-tab',
  Insurance = 'schedule-insurance-tab',
}

type State = {
  selectedTab?: SchedulePulseTab;
  appointmentListTabState?: {
    calendarDate?: string; // MM/DD/YYYY
    isUnconfirmedStatusSelected?: boolean;
  };
};

type Actions = {
  setSelectedTab: (tab: SchedulePulseTab) => void;
  setAppointmentListTabState: (state: State['appointmentListTabState']) => void;
  setState: (state: Partial<State>) => void;
};

type Store = State & Actions;

const initialState: State = {};

const useStore = createStoreWithPersist<Store>(
  (set) => ({
    ...initialState,
    setSelectedTab: (tab: SchedulePulseTab) => {
      set({ selectedTab: tab }, false, 'SET_SELECTED_TAB');
    },
    setAppointmentListTabState: (state) => {
      set(
        (prev) => ({
          appointmentListTabState: { ...prev.appointmentListTabState, ...state },
        }),
        false,
        'SET_APPOINTMENT_LIST_TAB_STATE'
      );
    },
    setState: (state) => set(state, false, 'SET_STATE'),
  }),
  {
    name: 'schedule-pulse-store',
  },
  {
    name: 'SchedulePulseStore',
    trace: true,
  }
);

export const useSchedulePulseShallowStore = createShallowStore(useStore);

// This wrapper will help sync schedule-pulse store state to calendar-v3 store state
// and it currently use for calendar header filter buttons to open schedule-pulse with specific filters
export const SyncSchedulePulseWithCalendarWrapper = ({ children }: PropsWithChildren) => {
  const { appointmentListTabState } = useSchedulePulseShallowStore('appointmentListTabState');
  const { setCalendarDate, setCalendarFilters, setIsForSchedulePulse } = useCalendarStore([
    'setCalendarDate',
    'setCalendarFilters',
    'setIsForSchedulePulse',
  ]);

  useEffect(() => {
    if (!appointmentListTabState?.calendarDate) return;
    setCalendarDate(appointmentListTabState.calendarDate);
    setCalendarFilters({ apptStatuses: appointmentListTabState?.isUnconfirmedStatusSelected ? ['unconfirmed'] : [] });
  }, [appointmentListTabState, setCalendarDate, setCalendarFilters]);

  useEffect(() => {
    setIsForSchedulePulse(true);
  }, [setIsForSchedulePulse]);

  return children;
};

export const openSchedulePulseWithFilters = (state: Partial<State>) => {
  useStore.getState().setState(state);
  dispatchTopBarTraySelectionEvent('schedulePulse');
};

export const openSchedulePulse = (tab: SchedulePulseTab) => openSchedulePulseWithFilters({ selectedTab: tab });
